// Trail — searchable, timestamped audit log. The whole point is the admin
// can scroll back in time and ask "what happened around 11:42 yesterday?".
// Keep the row tight; one line per event with details on hover/expand.
import { useEffect, useMemo, useState } from "react";
import { Search, Clock, RefreshCw, ChevronLeft, ChevronRight } from "lucide-react";

interface AuditRow {
  id: number;
  companyId: number | null;
  action: string;
  entity: string;
  entityId: number | null;
  details: string | null;
  performedBy: string | null;
  source: string | null;
  createdAt: string;
}

interface AuditResponse {
  total: number;
  rows: AuditRow[];
  entities: string[];
  limit: number;
  offset: number;
}

const PAGE_SIZE = 100;
const SINCE_PRESETS: Array<{ label: string; hours: number | null }> = [
  { label: "Any time", hours: null },
  { label: "Last 1h", hours: 1 },
  { label: "Last 24h", hours: 24 },
  { label: "Last 7d", hours: 24 * 7 },
  { label: "Last 30d", hours: 24 * 30 },
];

function formatTs(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString("en-IN", {
    day: "2-digit", month: "short", year: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
    hour12: false,
  });
}

function relativeTs(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return `${Math.max(1, Math.floor(ms / 1000))}s ago`;
  if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m ago`;
  if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)}h ago`;
  return `${Math.floor(ms / 86_400_000)}d ago`;
}

export default function AdminTimestampTrail() {
  const [data, setData] = useState<AuditResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [entity, setEntity] = useState("");
  const [sinceHours, setSinceHours] = useState<number | null>(null);
  const [page, setPage] = useState(0);

  // Debounce search input so we are not slamming the API on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 300);
    return () => clearTimeout(t);
  }, [q]);

  // Reset to page 0 when filters change.
  useEffect(() => { setPage(0); }, [debouncedQ, entity, sinceHours]);

  const sinceIso = useMemo(() => {
    if (sinceHours == null) return "";
    return new Date(Date.now() - sinceHours * 3600_000).toISOString();
  }, [sinceHours]);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (debouncedQ) params.set("q", debouncedQ);
      if (entity) params.set("entity", entity);
      if (sinceIso) params.set("since", sinceIso);
      params.set("limit", String(PAGE_SIZE));
      params.set("offset", String(page * PAGE_SIZE));
      const token = localStorage.getItem("admin_token") || "";
      const res = await fetch(`/api/admin/audit-log?${params.toString()}`, {
        headers: { "x-admin-token": token },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const j = await res.json();
      setData(j);
    } catch (e: any) {
      setError(e?.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [debouncedQ, entity, sinceIso, page]);

  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const showingFrom = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const showingTo = Math.min(total, (page + 1) * PAGE_SIZE);

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Trail</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Every recorded action across the platform, newest first. Search by anything you remember — entity, person, keyword, ID.
          </p>
        </div>
        <button
          onClick={load}
          className="flex items-center gap-2 px-3 py-1.5 text-sm rounded-md border hover:bg-muted"
        >
          <RefreshCw className="w-3.5 h-3.5" />
          Refresh
        </button>
      </div>

      <div className="rounded-lg border bg-card p-4 mb-4 grid gap-3 md:grid-cols-[1fr_220px_180px]">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search action, entity, details, performed by, ID…"
            className="w-full pl-9 pr-3 py-2 text-sm rounded-md border bg-background"
          />
        </div>
        <select
          value={entity}
          onChange={(e) => setEntity(e.target.value)}
          className="px-3 py-2 text-sm rounded-md border bg-background"
        >
          <option value="">All entities</option>
          {data?.entities.map((e) => (
            <option key={e} value={e}>{e}</option>
          ))}
        </select>
        <select
          value={sinceHours == null ? "" : String(sinceHours)}
          onChange={(e) => setSinceHours(e.target.value === "" ? null : Number(e.target.value))}
          className="px-3 py-2 text-sm rounded-md border bg-background"
        >
          {SINCE_PRESETS.map((p) => (
            <option key={p.label} value={p.hours == null ? "" : String(p.hours)}>{p.label}</option>
          ))}
        </select>
      </div>

      {error && (
        <div className="rounded-md border border-red-300 bg-red-50 text-red-800 px-4 py-3 text-sm mb-4">
          {error}
        </div>
      )}

      <div className="rounded-lg border bg-card overflow-hidden">
        <div className="px-4 py-2 border-b bg-muted/30 flex items-center justify-between text-xs text-muted-foreground">
          <span>
            {loading ? "Loading…" : `${total.toLocaleString("en-IN")} events · showing ${showingFrom}–${showingTo}`}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0 || loading}
              className="p-1 rounded hover:bg-muted disabled:opacity-40"
              aria-label="Previous page"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span>Page {page + 1} / {totalPages}</span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1 || loading}
              className="p-1 rounded hover:bg-muted disabled:opacity-40"
              aria-label="Next page"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        {data && data.rows.length === 0 && !loading && (
          <div className="px-6 py-16 text-center text-sm text-muted-foreground">
            No events match your filters.
          </div>
        )}

        <div className="divide-y">
          {data?.rows.map((r) => (
            <div key={r.id} className="px-4 py-3 grid grid-cols-[180px_1fr] gap-4 hover:bg-muted/30">
              <div className="text-xs text-muted-foreground">
                <div className="flex items-center gap-1.5 font-mono">
                  <Clock className="w-3 h-3" />
                  {formatTs(r.createdAt)}
                </div>
                <div className="text-[11px] mt-0.5 pl-4">{relativeTs(r.createdAt)}</div>
              </div>
              <div className="text-sm">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="px-1.5 py-0.5 rounded bg-primary/10 text-primary text-[11px] font-mono uppercase tracking-wide">
                    {r.action}
                  </span>
                  <span className="font-medium">{r.entity}</span>
                  {r.entityId != null && (
                    <span className="text-xs text-muted-foreground font-mono">#{r.entityId}</span>
                  )}
                  {r.companyId != null && (
                    <span className="text-xs text-muted-foreground">· company #{r.companyId}</span>
                  )}
                  {r.performedBy && (
                    <span className="text-xs text-muted-foreground">· by {r.performedBy}</span>
                  )}
                  {r.source && (
                    <span className="text-xs text-muted-foreground">· {r.source}</span>
                  )}
                </div>
                {r.details && (
                  <div className="text-xs text-muted-foreground mt-1 whitespace-pre-wrap break-words">
                    {r.details}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
